import { Locale, format } from 'date-fns'
import { SymptomGraphDataPoint, SymptomGraphEntry } from '@estercare/ester-shared'
import { MultiLineChartData, MultiLineChartLine } from '../../components/charts/type'
import { theme } from '../../theme/theme'

// Colour palette for the top symptoms overview, cycled per symptom index.
export const SYMPTOM_LINE_COLORS = [
  theme.color.grey4,
  theme.color.brightBlue,
  theme.color.yellow,
  theme.color.magenta,
  theme.color.orange,
]

// Colour palette for the per-category breakdown lines, cycled per line index.
const BREAKDOWN_LINE_COLORS = [
  theme.color.plumMid,
  theme.color.green,
  theme.color.brightBlue,
  theme.color.orange,
  theme.color.yellow,
]

// Y-axis ticks for a chart, derived from the symptom's maxValue (e.g. 0-10 or 0-30).
export const buildScale = (maxValue: number): number[] => {
  if (maxValue <= 10) return [0, 2, 4, 6, 8, 10]
  const step = Math.round(maxValue / 3)
  return [0, step, step * 2, maxValue]
}

const toDateLabels = (date: string, locale: Locale) => ({
  name: format(new Date(date), 'MMM yyyy', { locale }),
  nameMobile: format(new Date(date), 'MMM yy', { locale }),
})

const chronological = (dataPoints: SymptomGraphDataPoint[]) =>
  [...dataPoints].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

// ---------------------------------------------------------------------------
// General wellbeing — single line chart
// ---------------------------------------------------------------------------

export const buildGeneralWellbeingData = (symptom: SymptomGraphEntry | undefined, locale: Locale) => {
  const line = symptom?.lines[0]
  if (!line) return []
  return chronological(line.dataPoints).map((dp) => ({ ...toDateLabels(dp.date, locale), value: dp.value }))
}

// ---------------------------------------------------------------------------
// Top symptoms overview — one line per symptom, first line (severity) only
// ---------------------------------------------------------------------------

export const buildSymptomLines = (symptoms: SymptomGraphEntry[]): MultiLineChartLine[] =>
  symptoms.map((symptom, index) => ({
    dataKey: symptom.category,
    name: symptom.label,
    color: SYMPTOM_LINE_COLORS[index % SYMPTOM_LINE_COLORS.length] ?? theme.color.grey4,
  }))

export const buildTopSymptomsData = (symptoms: SymptomGraphEntry[], locale: Locale): MultiLineChartData[] => {
  const byDate = new Map<string, MultiLineChartData>()

  symptoms.forEach((symptom) => {
    const line = symptom.lines[0]
    if (!line) return

    line.dataPoints.forEach((dp) => {
      const point: MultiLineChartData = byDate.get(dp.date) ?? toDateLabels(dp.date, locale)
      point[symptom.category] = dp.value
      byDate.set(dp.date, point)
    })
  })

  return [...byDate.entries()]
    .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
    .map(([, point]) => point)
}

// ---------------------------------------------------------------------------
// Per-category breakdown — one line per tag
// ---------------------------------------------------------------------------

const lineDataKey = (index: number) => `line_${index}`

export const buildBreakdownLines = (symptom: SymptomGraphEntry): MultiLineChartLine[] =>
  symptom.lines.map((line, index) => ({
    dataKey: lineDataKey(index),
    name: line.label || symptom.label,
    color: BREAKDOWN_LINE_COLORS[index % BREAKDOWN_LINE_COLORS.length] ?? theme.color.plumMid,
  }))

export const buildBreakdownData = (symptom: SymptomGraphEntry, locale: Locale): MultiLineChartData[] => {
  const byDate = new Map<string, MultiLineChartData>()

  symptom.lines.forEach((line, index) => {
    const key = lineDataKey(index)

    line.dataPoints.forEach((dp) => {
      const point: MultiLineChartData = byDate.get(dp.date) ?? toDateLabels(dp.date, locale)
      point[key] = dp.value
      byDate.set(dp.date, point)
    })
  })

  return [...byDate.entries()]
    .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
    .map(([, point]) => point)
}
